import React from "react";

import { Fancybox as NativeFancybox } from "@fancyapps/ui/dist/fancybox.esm.js";
import "@fancyapps/ui/dist/fancybox.css";

class Fancybox extends React.Component {

    constructor(props) {
        super(props);

        const delegate = this.props.delegate || "[data-fancybox]";
        const opts = {
            ...props.options,
            // Отключаем все анимации для улучшения производительности
            animated: false,
            showClass: false,
            hideClass: false,
            dragToClose: false,
            wheel: false,
            Thumbs: {
                autoStart: true,
                ...(props.options && props.options.Thumbs ? props.options.Thumbs : {}),
            },
            on: {
                ready: (fancybox, slide) => {
                    // Проверяем, что slide существует
                    if (slide && slide.rotation === undefined) {
                        slide.rotation = 0;
                        slide.flipX = false;
                        slide.flipY = false;
                    }
                    
                    // Добавляем кастомные кнопки в тулбар
                    setTimeout(() => {
                        this.addCustomButtons(fancybox);
                        this.setupImageObserver(slide);
                    }, 100);
                },
                refresh: (fancybox, slide) => {
                    // Восстанавливаем поворот после обновления (например, при zoom)
                    this.restoreRotationWithDelay(slide, 100, true); // true = без анимации
                },
                done: (fancybox, slide) => {
                    // Восстанавливаем поворот после завершения анимации
                    this.restoreRotationWithDelay(slide, 150, true); // true = без анимации
                    this.setupImageObserver(slide);
                },
                reveal: (fancybox, slide) => {
                    // Восстанавливаем поворот после показа слайда
                    this.restoreRotationWithDelay(slide, 200, true); // true = без анимации
                },
                closing: (fancybox) => {
                    // Убираем фокус со всех кнопок перед закрытием
                    const container = fancybox.$container;
                    if (container) {
                        const focusedElement = container.querySelector(':focus');
                        if (focusedElement) {
                            focusedElement.blur();
                        }
                        
                        // Убираем фокус со всех кнопок в контейнере
                        const buttons = container.querySelectorAll('button');
                        buttons.forEach(button => {
                            button.blur();
                            button.setAttribute('tabindex', '-1');
                        });
                    }
                },
                destroy: (fancybox) => {
                    // Очищаем все observers при уничтожении
                    this.imageObservers.forEach(observer => observer.disconnect());
                    this.imageObservers.clear();
                },
                ...props.options?.on
            }
        };

        NativeFancybox.bind(delegate, opts);
        
        // Добавляем стили
        this.addRotationStyles();
        
        // Добавляем обработчики событий
        this.attachEventListeners();
        
        // Хранилище для MutationObserver
        this.imageObservers = new Map();
    }

    setupImageObserver = (slide) => {
        if (!slide || !slide.$content) return;
        
        const img = slide.$content.querySelector('img');
        if (!img) return;
        
        // Удаляем старый observer если есть
        const existingObserver = this.imageObservers.get(slide);
        if (existingObserver) {
            existingObserver.disconnect();
        }
        
        // Создаем новый observer для отслеживания изменений атрибута style
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                    // Проверяем, что поворот был сброшен
                    const currentTransform = img.style.transform;
                    const hasRotation = slide.rotation || slide.flipX || slide.flipY;
                    
                    if (hasRotation && (!currentTransform || 
                        (!currentTransform.includes('rotate') && slide.rotation) ||
                        (!currentTransform.includes('scaleX') && slide.flipX) ||
                        (!currentTransform.includes('scaleY') && slide.flipY))) {
                        // Восстанавливаем поворот без анимации
                        setTimeout(() => {
                            this.applyTransform(img, slide, true); // true = без анимации
                        }, 50);
                    }
                }
            });
        });
        
        observer.observe(img, {
            attributes: true,
            attributeFilter: ['style']
        });
        
        this.imageObservers.set(slide, observer);
    }

    restoreRotationWithDelay = (slide, delay, noAnimation = false) => {
        setTimeout(() => {
            if (slide && slide.$content) {
                const img = slide.$content.querySelector('img');
                if (img && (slide.rotation || slide.flipX || slide.flipY)) {
                    this.applyTransform(img, slide, noAnimation);
                }
            }
        }, delay);
        
        // Дополнительная попытка через большую задержку
        setTimeout(() => {
            if (slide && slide.$content) {
                const img = slide.$content.querySelector('img');
                if (img && (slide.rotation || slide.flipX || slide.flipY)) {
                    this.applyTransform(img, slide, noAnimation);
                }
            }
        }, delay + 300);
    }

    attachEventListeners = () => {
        // Обработчик для добавления кнопок при открытии Fancybox
        document.addEventListener('click', (e) => {
            const fancyboxTrigger = e.target.closest('[data-fancybox]');
            if (fancyboxTrigger) {
                // Ждём инициализации Fancybox
                setTimeout(() => {
                    const fancybox = NativeFancybox.getInstance();
                    if (fancybox) {
                        this.addCustomButtons(fancybox);
                        this.initSlideRotation(fancybox);
                    }
                }, 200);
            }
        });

        // Обработчик кликов по кнопкам поворота
        document.addEventListener('click', this.handleRotationClick, true);
        
        // Обработчик кликов по изображению для отслеживания zoom
        document.addEventListener('click', this.handleImageClick, true);
        
        // Обработчики для перетаскивания
        document.addEventListener('mousedown', this.handleDragStart, true);
        document.addEventListener('touchstart', this.handleDragStart, true);
        document.addEventListener('mouseup', this.handleDragEnd, true);
        document.addEventListener('touchend', this.handleDragEnd, true);
        
        // Обработчик клавиатуры для предотвращения конфликтов фокуса
        document.addEventListener('keydown', this.handleKeyDown, true);
        
        // Обработчик для отслеживания закрытия Fancybox
        document.addEventListener('click', this.handleCloseClick, true);
    }

    handleDragStart = (e) => {
        const img = e.target.closest('.fancybox__content img');
        if (img) {
            const fancybox = NativeFancybox.getInstance();
            if (fancybox) {
                const slide = fancybox.getSlide();
                if (slide && (slide.rotation || slide.flipX || slide.flipY)) {
                    // Запоминаем, что началось перетаскивание
                    slide.isDragging = true;
                    
                    // Используем requestAnimationFrame для плавного обновления трансформаций
                    const updateTransform = () => {
                        if (slide.isDragging && img) {
                            // Получаем текущий transform от Fancybox
                            const currentTransform = img.style.transform || '';
                            
                            // Создаем новый transform с нашими поворотами
                            let newTransform = currentTransform;
                            
                            // Удаляем старые rotate и scale трансформации от нас
                            newTransform = newTransform.replace(/rotate\([^)]+\)/g, '');
                            newTransform = newTransform.replace(/scaleX\(-1\)/g, '');
                            newTransform = newTransform.replace(/scaleY\(-1\)/g, '');
                            
                            // Добавляем наши трансформации
                            const ourTransforms = [];
                            if (slide.rotation && slide.rotation !== 0) {
                                ourTransforms.push(`rotate(${slide.rotation}deg)`);
                            }
                            if (slide.flipX) {
                                ourTransforms.push('scaleX(-1)');
                            }
                            if (slide.flipY) {
                                ourTransforms.push('scaleY(-1)');
                            }
                            
                            if (ourTransforms.length > 0) {
                                newTransform = (newTransform.trim() + ' ' + ourTransforms.join(' ')).trim();
                            }
                            
                            img.style.transform = newTransform;
                            img.style.transformOrigin = 'center center';
                            
                            // Продолжаем обновление если еще перетаскиваем
                            if (slide.isDragging) {
                                requestAnimationFrame(updateTransform);
                            }
                        }
                    };
                    
                    // Запускаем обновление
                    requestAnimationFrame(updateTransform);
                }
            }
        }
    }

    handleDragEnd = (e) => {
        const fancybox = NativeFancybox.getInstance();
        if (fancybox) {
            const slide = fancybox.getSlide();
            if (slide && slide.isDragging) {
                slide.isDragging = false;
                
                // Финальное применение трансформации
                setTimeout(() => {
                    const img = slide.$content?.querySelector('img');
                    if (img) {
                        this.applyTransform(img, slide, true);
                    }
                }, 50);
            }
        }
    }

    handleImageClick = (e) => {
        const img = e.target.closest('.fancybox__content img');
        if (img) {
            const fancybox = NativeFancybox.getInstance();
            if (fancybox) {
                const slide = fancybox.getSlide();
                if (slide && (slide.rotation || slide.flipX || slide.flipY)) {
                    // Сохраняем состояние поворота перед zoom без анимации
                    setTimeout(() => {
                        this.restoreRotationWithDelay(slide, 300, true);
                    }, 100);
                }
            }
        }
    }

    initSlideRotation = (fancybox) => {
        const slide = fancybox.getSlide();
        if (slide && slide.rotation === undefined) {
            slide.rotation = 0;
            slide.flipX = false;
            slide.flipY = false;
        }
    }

    addCustomButtons = (fancybox) => {
        const toolbar = fancybox.$container?.querySelector('.fancybox__toolbar');
        if (!toolbar || toolbar.querySelector('.rotation-buttons-added')) return;
        
        // Создаем контейнер для кнопок поворота
        const rotationContainer = document.createElement('div');
        rotationContainer.className = 'fancybox__toolbar-group rotation-buttons-added';
        rotationContainer.style.cssText = 'display: inline-flex; align-items: center; height: 44px; margin-left: auto; vertical-align: middle;';
        
        // Кнопка поворота против часовой стрелки
        const rotateCCWBtn = document.createElement('button');
        rotateCCWBtn.className = 'fancybox__button fancybox__button--rotateCCW';
        rotateCCWBtn.title = 'Повернуть против часовой стрелки';
        rotateCCWBtn.innerHTML = '↺';
        rotateCCWBtn.tabIndex = 0;
        rotateCCWBtn.type = 'button';
        
        // Кнопка поворота по часовой стрелке
        const rotateCWBtn = document.createElement('button');
        rotateCWBtn.className = 'fancybox__button fancybox__button--rotateCW';
        rotateCWBtn.title = 'Повернуть по часовой стрелке';
        rotateCWBtn.innerHTML = '↻';
        rotateCWBtn.tabIndex = 0;
        rotateCWBtn.type = 'button';
        
        // Кнопка отражения по горизонтали
        const flipXBtn = document.createElement('button');
        flipXBtn.className = 'fancybox__button fancybox__button--flipX';
        flipXBtn.title = 'Отразить по горизонтали';
        flipXBtn.innerHTML = '⇄';
        flipXBtn.tabIndex = 0;
        flipXBtn.type = 'button';
        
        // Кнопка отражения по вертикали
        const flipYBtn = document.createElement('button');
        flipYBtn.className = 'fancybox__button fancybox__button--flipY';
        flipYBtn.title = 'Отразить по вертикали';
        flipYBtn.innerHTML = '⇅';
        flipYBtn.tabIndex = 0;
        flipYBtn.type = 'button';
        
        // Добавляем кнопки в контейнер
        rotationContainer.appendChild(rotateCCWBtn);
        rotationContainer.appendChild(rotateCWBtn);
        rotationContainer.appendChild(flipXBtn);
        rotationContainer.appendChild(flipYBtn);
        
        // Добавляем контейнер в тулбар
        toolbar.appendChild(rotationContainer);
    }

    addRotationStyles = () => {
        if (!document.querySelector('#fancybox-rotation-styles')) {
            const style = document.createElement('style');
            style.id = 'fancybox-rotation-styles';
            style.textContent = `
                .fancybox__content img {
                    transition: none !important;
                    transform-origin: center center !important;
                }
                
                /* Класс для отключения анимации при автоматическом восстановлении */
                .fancybox__content img.no-transition {
                    transition: none !important;
                }
                
                .fancybox__button--rotateCCW,
                .fancybox__button--rotateCW,
                .fancybox__button--flipX,
                .fancybox__button--flipY {
                    font-size: 18px !important;
                    line-height: 1 !important;
                    width: 44px !important;
                    height: 44px !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    background: transparent !important;
                    border: none !important;
                    color: #fff !important;
                    cursor: pointer !important;
                    border-radius: 3px;
                    min-width: 44px !important;
                    min-height: 44px !important;
                    outline: none !important;
                    position: relative;
                    margin: 0 !important;
                    padding: 0 !important;
                    vertical-align: middle !important;
                    box-sizing: border-box !important;
                    flex-shrink: 0 !important;
                    align-self: center !important;
                }
                
                .fancybox__button--rotateCCW:hover,
                .fancybox__button--rotateCW:hover,
                .fancybox__button--flipX:hover,
                .fancybox__button--flipY:hover {
                    background-color: rgba(255, 255, 255, 0.2) !important;
                }
                
                .fancybox__button--rotateCCW:focus,
                .fancybox__button--rotateCW:focus,
                .fancybox__button--flipX:focus,
                .fancybox__button--flipY:focus {
                    background-color: rgba(255, 255, 255, 0.3) !important;
                    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5) !important;
                }
                
                /* Предотвращаем проблемы с aria-hidden */
                .fancybox__button--rotateCCW[tabindex="-1"],
                .fancybox__button--rotateCW[tabindex="-1"],
                .fancybox__button--flipX[tabindex="-1"],
                .fancybox__button--flipY[tabindex="-1"] {
                    pointer-events: none;
                    opacity: 0.7;
                }
                
                /* Общие стили для всех кнопок Fancybox для предотвращения конфликтов фокуса */
                .fancybox__container.is-closing .fancybox__button,
                .fancybox__container[aria-hidden="true"] .fancybox__button {
                    pointer-events: none !important;
                    outline: none !important;
                }
                
                .fancybox__container.is-closing .fancybox__button:focus,
                .fancybox__container[aria-hidden="true"] .fancybox__button:focus {
                    outline: none !important;
                    box-shadow: none !important;
                }
                
                .rotation-buttons-added {
                    margin-left: auto;
                    display: inline-flex !important;
                    align-items: center !important;
                    height: 44px !important;
                    gap: 2px;
                    vertical-align: middle !important;
                    flex-shrink: 0 !important;
                }
                
                /* Увеличиваем ВСЕ кнопки на мобильных устройствах */
                @media (max-width: 768px) {
                    .fancybox__button {
                        width: 56px !important;
                        height: 56px !important;
                        min-width: 56px !important;
                        min-height: 56px !important;
                        font-size: 20px !important;
                    }
                    
                    .fancybox__button--rotateCCW,
                    .fancybox__button--rotateCW,
                    .fancybox__button--flipX,
                    .fancybox__button--flipY {
                        font-size: 22px !important;
                    }
                    
                    .rotation-buttons-added {
                        gap: 4px;
                        margin: 0 8px;
                    }
                    
                    .fancybox__toolbar {
                        padding: 8px 12px !important;
                    }
                }
                
                /* Для очень маленьких экранов */
                @media (max-width: 480px) {
                    .fancybox__button {
                        width: 60px !important;
                        height: 60px !important;
                        min-width: 60px !important;
                        min-height: 60px !important;
                        font-size: 22px !important;
                    }
                    
                    .fancybox__button--rotateCCW,
                    .fancybox__button--rotateCW,
                    .fancybox__button--flipX,
                    .fancybox__button--flipY {
                        font-size: 24px !important;
                    }
                    
                    .fancybox__toolbar {
                        padding: 10px 15px !important;
                    }
                }
            `;
            document.head.appendChild(style);
        }
    }

    handleRotationClick = (e) => {
        const buttonClass = e.target.closest('.fancybox__button--rotateCCW, .fancybox__button--rotateCW, .fancybox__button--flipX, .fancybox__button--flipY');
        if (!buttonClass) {
            return;
        }

        // Предотвращаем всплытие события и действие по умолчанию
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const fancybox = NativeFancybox.getInstance();
        if (!fancybox) return;

        const slide = fancybox.getSlide();
        if (!slide) return;

        const img = slide.$content?.querySelector('img');
        if (!img) return;

        // Инициализируем значения если их нет
        if (slide.rotation === undefined) slide.rotation = 0;
        if (slide.flipX === undefined) slide.flipX = false;
        if (slide.flipY === undefined) slide.flipY = false;

        if (buttonClass.classList.contains('fancybox__button--rotateCCW')) {
            slide.rotation = (slide.rotation - 90) % 360;
        } else if (buttonClass.classList.contains('fancybox__button--rotateCW')) {
            slide.rotation = (slide.rotation + 90) % 360;
        } else if (buttonClass.classList.contains('fancybox__button--flipX')) {
            slide.flipX = !slide.flipX;
        } else if (buttonClass.classList.contains('fancybox__button--flipY')) {
            slide.flipY = !slide.flipY;
        }

        // При ручном повороте используем анимацию
        this.applyTransform(img, slide, false);
        
        // Убираем фокус с кнопки и переносим на контейнер, чтобы избежать проблем с aria-hidden
        buttonClass.blur();
        const container = fancybox.$container;
        if (container) {
            // Убираем tabindex с кнопки временно
            buttonClass.setAttribute('tabindex', '-1');
            setTimeout(() => {
                buttonClass.setAttribute('tabindex', '0');
            }, 100);
        }
    }

    applyTransform = (img, slide, noAnimation = false) => {
        if (!img || !slide) return;
        
        // Временно отключаем анимацию если нужно
        if (noAnimation) {
            img.classList.add('no-transition');
        }
        
        // Получаем текущий transform от Fancybox
        const currentTransform = img.style.transform || '';
        
        // Создаем новый transform, сохраняя Fancybox трансформации
        let newTransform = currentTransform;
        
        // Удаляем наши старые трансформации
        newTransform = newTransform.replace(/rotate\([^)]+\)/g, '');
        newTransform = newTransform.replace(/scaleX\(-1\)/g, '');
        newTransform = newTransform.replace(/scaleY\(-1\)/g, '');
        
        // Очищаем лишние пробелы
        newTransform = newTransform.replace(/\s+/g, ' ').trim();
        
        // Добавляем наши трансформации
        const ourTransforms = [];
        if (slide.rotation && slide.rotation !== 0) {
            ourTransforms.push(`rotate(${slide.rotation}deg)`);
        }
        if (slide.flipX) {
            ourTransforms.push('scaleX(-1)');
        }
        if (slide.flipY) {
            ourTransforms.push('scaleY(-1)');
        }
        
        // Объединяем трансформации
        if (ourTransforms.length > 0) {
            if (newTransform) {
                newTransform = newTransform + ' ' + ourTransforms.join(' ');
            } else {
                newTransform = ourTransforms.join(' ');
            }
        }
        
        img.style.transform = newTransform;
        img.style.transformOrigin = 'center center';
        
        // Возвращаем анимацию через небольшую задержку
        if (noAnimation) {
            setTimeout(() => {
                img.classList.remove('no-transition');
            }, 50);
        }
    }

    handleKeyDown = (e) => {
        // Если нажата клавиша Escape в Fancybox
        if (e.key === 'Escape') {
            const fancybox = NativeFancybox.getInstance();
            if (fancybox && fancybox.$container) {
                // Убираем фокус перед закрытием
                const focusedElement = fancybox.$container.querySelector(':focus');
                if (focusedElement) {
                    focusedElement.blur();
                }
            }
        }
    }

    handleCloseClick = (e) => {
        // Если кликнули по кнопке закрытия или фону
        const closeButton = e.target.closest('.fancybox__button--close');
        const backdrop = e.target.closest('.fancybox__backdrop');
        
        if (closeButton || backdrop) {
            const fancybox = NativeFancybox.getInstance();
            if (fancybox && fancybox.$container) {
                // Убираем фокус перед закрытием
                setTimeout(() => {
                    const container = fancybox.$container
                    if (!container) return
                    const focusedElement = container.querySelector(':focus');
                    if (focusedElement) {
                        focusedElement.blur();
                    }
                }, 10);
            }
        }
    }

    componentWillUnmount() {
        // Отключаем все observers
        this.imageObservers.forEach(observer => observer.disconnect());
        this.imageObservers.clear();
        
        // Удаляем event listeners
        document.removeEventListener('click', this.handleRotationClick, true);
        document.removeEventListener('click', this.handleImageClick, true);
        document.removeEventListener('mousedown', this.handleDragStart, true);
        document.removeEventListener('touchstart', this.handleDragStart, true);
        document.removeEventListener('mouseup', this.handleDragEnd, true);
        document.removeEventListener('touchend', this.handleDragEnd, true);
        document.removeEventListener('keydown', this.handleKeyDown, true);
        document.removeEventListener('click', this.handleCloseClick, true);
        //NativeFancybox.destroy();
    }

    render() {
        return <>{this.props.children}</>;
    }
}

export default Fancybox;
